import IconCircleQuestion from '@mnd-frontend/icons/components/CircleQuestion';
import { MEDIA_SMALL } from '@mnd-frontend/ui/shared/tokens';
import { useEffect, useState } from 'react';
import { useWidget, WIDGETS } from '../../../../../contexts/WidgetContext';
import useHotjarTrigger from '../../../../../hooks/useHotjarTrigger';
import { track } from '../../../../../lib/tracker';
import WidgetTrigger from '../WidgetTrigger';
import { AnswerBody, MainBody, PanelFooter, PanelHeader } from './components';
import SidePanel from './components/SidePanel';
import HELP_PANEL_CONTENT, { type HelpContent } from './helpPanelContent';

type Props = {
  contentKey: keyof HelpContent;
};

const HelpPanel = ({ contentKey }: Props) => {
  const { widget, setWidget } = useWidget();
  const [isAnswerOpen, toggleAnswerOpen] = useState(false);
  const [chosenQuestion, setChosenQuestion] = useState<null | {
    question: string;
    category: string;
  }>(null);
  const [showComponent, setShowComponent] = useState(true);
  const [haveUserTriggered, setUserTriggered] = useState(false);
  const content = HELP_PANEL_CONTENT[contentKey];

  const hotjar_trigger = content.hotjarTrigger;

  // biome-ignore lint/correctness/useExhaustiveDependencies: I assume we need this to make sure if content changes we react??
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < MEDIA_SMALL;
      setShowComponent(!isMobile);
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [content]);

  const onQuestionClick = (question: any) => {
    toggleAnswerOpen(true);
    track({
      event: 'Help Panel: Clicked Article',
      traits: {
        article: question.question,
      },
    });
    setChosenQuestion(question);
  };

  const onBackClick = () => {
    setChosenQuestion(null);
    toggleAnswerOpen(false);
  };

  const onPanelClose = () => {
    toggleAnswerOpen(false);
    setChosenQuestion(null);
    setWidget(null);
  };

  const onClick = () => {
    setUserTriggered(true);
    track({ event: 'Help Panel: Clicked Start' });
    setWidget(WIDGETS.HELP);
  };

  const onExternalLinkClick = (article: string) => {
    track({
      event: 'Help Panel: Clicked Help Center',
      traits: {
        destination: article,
      },
    });
  };

  useHotjarTrigger(hotjar_trigger, !haveUserTriggered);

  if (!showComponent) return null;
  return (
    <>
      <WidgetTrigger
        svg={<IconCircleQuestion />}
        open={widget === WIDGETS.HELP}
        onClick={onClick}
      />
      <SidePanel
        isOpen={widget === WIDGETS.HELP}
        onClose={onPanelClose}
        header={
          <PanelHeader
            header={content.header}
            showBackButton={isAnswerOpen}
            onBackClick={onBackClick}
          />
        }
        footer={<PanelFooter onExternalLinkClick={onExternalLinkClick} />}
      >
        {isAnswerOpen ? (
          <AnswerBody content={content} chosenQuestion={chosenQuestion} />
        ) : (
          <MainBody
            content={content}
            onQuestionClick={onQuestionClick}
            onExternalLinkClick={onExternalLinkClick}
          />
        )}
      </SidePanel>
    </>
  );
};

export default HelpPanel;
